import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Search, Edit2, Gift, Ban, CheckCircle, XCircle, Trash2, CreditCard, Plus, UserPlus } from "lucide-react";

export default function AdminCustomers() {
  const { t } = useI18n();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [freeMonths, setFreeMonths] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: "", email: "", password: "", company_name: "", role: "user", language: "nl" });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = () => {
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/admin/customers/${editing.id}`, {
        name: editing.name, company_name: editing.company_name, role: editing.role,
        language: editing.language, vat_number: editing.vat_number,
        address: editing.address, phone: editing.phone,
      });
      if (freeMonths && parseInt(freeMonths) > 0 && editing.subscription?.id) {
        await api.put(`/admin/subscriptions/${editing.subscription.id}`, { free_months: parseInt(freeMonths) });
        toast.success(`${freeMonths} gratis maand(en) toegekend`);
      }
      toast.success("Klant bijgewerkt");
      setEditing(null);
      setFreeMonths("");
      fetchCustomers();
    } catch { toast.error("Fout"); }
  };

  const handleBlock = async (id) => {
    if (!window.confirm(t("admin.block_user"))) return;
    await api.post(`/admin/customers/${id}/block`);
    toast.success("Gebruiker geblokkeerd");
    fetchCustomers();
  };

  const handleUnblock = async (id) => {
    await api.post(`/admin/customers/${id}/unblock`);
    toast.success("Gebruiker gedeblokkeerd");
    fetchCustomers();
  };

  const handleCancelSub = async (id) => {
    if (!window.confirm(t("admin.stop_sub"))) return;
    await api.post(`/admin/customers/${id}/cancel-subscription`);
    toast.success("Abonnement gestopt");
    fetchCustomers();
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("admin.delete_user_confirm"))) return;
    await api.delete(`/admin/customers/${id}`);
    toast.success("Gebruiker verwijderd");
    fetchCustomers();
  };

  const filtered = customers.filter((c) =>
    (c.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (c.email || "").toLowerCase().includes(search.toLowerCase()) ||
    (c.company_name || "").toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="admin-customers-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("admin.customers")}</h1>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{customers.length} {t("admin.total")}</Badge>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-[#2CA8AC] hover:bg-[#248F92]" data-testid="add-customer-btn">
                <UserPlus className="w-4 h-4 mr-2" />{t("admin.create_customer")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{t("admin.create_customer")}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>{t("admin.name")} *</Label><Input value={newCustomer.name} onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})} className="mt-1" data-testid="new-customer-name" /></div>
                  <div><Label>{t("admin.email")} *</Label><Input type="email" value={newCustomer.email} onChange={(e) => setNewCustomer({...newCustomer, email: e.target.value})} className="mt-1" data-testid="new-customer-email" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>{t("admin.password")} *</Label><Input type="password" value={newCustomer.password} onChange={(e) => setNewCustomer({...newCustomer, password: e.target.value})} className="mt-1" data-testid="new-customer-password" /></div>
                  <div><Label>{t("admin.company_name")}</Label><Input value={newCustomer.company_name} onChange={(e) => setNewCustomer({...newCustomer, company_name: e.target.value})} className="mt-1" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>{t("admin.role")}</Label>
                    <Select value={newCustomer.role} onValueChange={(v) => setNewCustomer({...newCustomer, role: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="super_admin">Super Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>{t("admin.language")}</Label>
                    <Select value={newCustomer.language} onValueChange={(v) => setNewCustomer({...newCustomer, language: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nl">Nederlands</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="fr">Français</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreate(false)}>Annuleren</Button>
                <Button onClick={async () => {
                  try {
                    await api.post("/admin/customers", newCustomer);
                    toast.success(t("admin.create_customer") + " - OK");
                    setShowCreate(false);
                    setNewCustomer({ name: "", email: "", password: "", company_name: "", role: "user", language: "nl" });
                    fetchCustomers();
                  } catch (err) { toast.error(err.response?.data?.detail || t("common.error")); }
                }} className="bg-blue-600" disabled={!newCustomer.name || !newCustomer.email || !newCustomer.password} data-testid="create-customer-btn">{t("admin.add")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("admin.search_customer")} className="pl-9" data-testid="admin-customer-search" />
      </div>

      <Card className="bg-card border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("admin.name")}</TableHead>
              <TableHead>{t("admin.email")}</TableHead>
              <TableHead>{t("admin.company")}</TableHead>
              <TableHead>{t("admin.role")}</TableHead>
              <TableHead>{t("admin.subscription")}</TableHead>
              <TableHead>{t("admin.language")}</TableHead>
              <TableHead className="text-right">{t("admin.products_count")}</TableHead>
              <TableHead className="text-right">{t("admin.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <TableRow key={c.id} className="table-row-hover" data-testid={`admin-customer-${c.id}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{c.email}</TableCell>
                <TableCell>{c.company_name || "-"}</TableCell>
                <TableCell><Badge variant={c.role === "super_admin" ? "destructive" : c.role === "admin" ? "default" : "secondary"}>{c.role}</Badge></TableCell>
                <TableCell>
                  {c.subscription ? (
                    <div>
                      <Badge variant="outline">{c.subscription.plan_name}</Badge>
                      <span className="text-xs text-muted-foreground ml-1 capitalize">({c.subscription.frequency})</span>
                      <Badge variant={c.subscription.status === "active" ? "default" : "secondary"} className={`ml-1 text-xs ${c.subscription.status === "active" ? "bg-emerald-500/20 text-emerald-400 border-0" : ""}`}>{c.subscription.status}</Badge>
                    </div>
                  ) : "-"}
                </TableCell>
                <TableCell className="uppercase text-xs">{c.language || "nl"}</TableCell>
                <TableCell className="text-right font-mono-data">{c.product_count}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditing({...c}); setFreeMonths(""); }} title="Bewerken" data-testid={`edit-customer-${c.id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    {c.status === "blocked" ? (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-500" onClick={() => handleUnblock(c.id)} title="Deblokkeren">
                        <CheckCircle className="w-3.5 h-3.5" />
                      </Button>
                    ) : (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-500" onClick={() => handleBlock(c.id)} title="Blokkeren">
                        <Ban className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {c.subscription?.status === "active" && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-orange-500" onClick={() => handleCancelSub(c.id)} title="Abonnement stoppen">
                        <XCircle className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(c.id)} title="Verwijderen">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {editing && (
        <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{t("admin.edit_customer")}: {editing.name}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>{t("admin.name")}</Label><Input value={editing.name || ""} onChange={(e) => setEditing({...editing, name: e.target.value})} className="mt-1" /></div>
                <div><Label>{t("admin.company")}</Label><Input value={editing.company_name || ""} onChange={(e) => setEditing({...editing, company_name: e.target.value})} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>{t("admin.vat_number")}</Label><Input value={editing.vat_number || ""} onChange={(e) => setEditing({...editing, vat_number: e.target.value})} className="mt-1" /></div>
                <div><Label>{t("admin.phone")}</Label><Input value={editing.phone || ""} onChange={(e) => setEditing({...editing, phone: e.target.value})} className="mt-1" /></div>
              </div>
              <div><Label>{t("admin.address")}</Label><Input value={editing.address || ""} onChange={(e) => setEditing({...editing, address: e.target.value})} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Rol</Label>
                  <Select value={editing.role} onValueChange={(v) => setEditing({...editing, role: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Taal</Label>
                  <Select value={editing.language || "nl"} onValueChange={(v) => setEditing({...editing, language: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nl">Nederlands</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="fr">Francais</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Subscription info */}
              {editing.subscription && (
                <div className="border border-border rounded-lg p-4 space-y-2">
                  <h4 className="font-semibold text-sm">{t("admin.subscription")}</h4>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div><span className="text-muted-foreground">{t("admin.plan")}:</span> <strong>{editing.subscription.plan_name}</strong></div>
                    <div><span className="text-muted-foreground">{t("admin.frequency")}:</span> <strong className="capitalize">{editing.subscription.frequency}</strong></div>
                    <div><span className="text-muted-foreground">{t("admin.status")}:</span> <strong>{editing.subscription.status}</strong></div>
                  </div>
                  {editing.subscription.renewal_date && (
                    <p className="text-xs text-muted-foreground">{t("admin.renewal")}: {new Date(editing.subscription.renewal_date).toLocaleDateString("nl-NL")}</p>
                  )}
                </div>
              )}

              {/* Free months */}
              <div className="border border-emerald-500/30 rounded-lg p-4 bg-emerald-500/5">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-4 h-4 text-emerald-500" />
                  <h4 className="font-semibold text-sm">{t("admin.free_months_title")}</h4>
                </div>
                <p className="text-xs text-muted-foreground mb-2">{t("admin.free_months_desc")}</p>
                <div className="flex items-center gap-2">
                  <Input type="number" min="0" max="12" value={freeMonths} onChange={(e) => setFreeMonths(e.target.value)} placeholder="0" className="w-20 h-8" />
                  <span className="text-sm text-muted-foreground">{t("admin.free_months")}</span>
                </div>
              </div>

              <div className="text-xs text-muted-foreground">
                <p>Producten: {editing.product_count} | Shops: {editing.shop_count} | Facturen: {editing.invoice_count}</p>
                <p>{t("admin.registered")}: {editing.created_at ? new Date(editing.created_at).toLocaleDateString("nl-NL") : "-"}</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>{t("admin.cancel")}</Button>
              <Button onClick={handleUpdate} className="bg-blue-600" data-testid="save-customer-btn">{t("admin.save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
